import { useState, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../../context";
import { useTranslation } from "react-i18next";
import useFetchObjects from "../../../api/useFetchObjects";
import useAdd from "../../../api/useAdd";
import useUpdate from "../../../api/useUpdate";
import useDelete from "../../../api/useDelete";
import axios from "axios";
import { toast } from "react-toastify";
import {
  Box,
  Paper,
  Typography,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Grid,
  Autocomplete,
  Checkbox,
  CircularProgress,
} from "@mui/material";
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, ArrowBack as BackIcon, PersonAdd as EnrollIcon } from "@mui/icons-material";
import TermFormDialog from "./TermFormDialog";

const userDisplay = (u) => (u ? `${u.first_name || ""} ${u.last_name || ""}`.trim() || u.username : "—");

export default function CurriculumPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const auth = useAuth();
  const token = auth?.user?.token || "";
  const [selectedTerm, setSelectedTerm] = useState(null);
  const [selectedSemester, setSelectedSemester] = useState(null);
  const [termDialogOpen, setTermDialogOpen] = useState(false);
  const [editingTerm, setEditingTerm] = useState(null);
  const [subjectDialogOpen, setSubjectDialogOpen] = useState(false);
  const [editingSubject, setEditingSubject] = useState(null);
  const [enrollDialogOpen, setEnrollDialogOpen] = useState(false);
  const [subjectForm, setSubjectForm] = useState({ name: "", code: "", mentor: "" });
  const [enrollSearch, setEnrollSearch] = useState("");
  const [selectedAcademics, setSelectedAcademics] = useState([]);
  const [savingEnroll, setSavingEnroll] = useState(false);

  const { data: termsData } = useFetchObjects(
    ["terms-curriculum"],
    "terms/?page_size=100&ordering=-year,-start_date",
    token
  );
  const terms = useMemo(() => {
    const raw = termsData?.results ?? termsData ?? [];
    return Array.isArray(raw) ? raw : [];
  }, [termsData]);

  const { data: semestersData } = useFetchObjects(
    ["semesters-term", selectedTerm?.id],
    selectedTerm?.id ? `semesters/?term=${selectedTerm.id}&page_size=10` : "",
    token,
    { enabled: !!selectedTerm?.id }
  );
  const semesters = useMemo(() => {
    const raw = semestersData?.results ?? semestersData ?? [];
    return Array.isArray(raw) ? raw : [];
  }, [semestersData]);

  const { data: subjectsData } = useFetchObjects(
    ["subjects-semester", selectedSemester?.id],
    selectedSemester?.id ? `subjects/?semester=${selectedSemester.id}&page_size=200` : "",
    token,
    { enabled: !!selectedSemester?.id }
  );
  const subjects = useMemo(() => {
    const raw = subjectsData?.results ?? subjectsData ?? [];
    return Array.isArray(raw) ? raw : [];
  }, [subjectsData]);

  const { data: enrollmentsData } = useFetchObjects(
    ["semester-enrollments", selectedSemester?.id],
    selectedSemester?.id ? `semester-enrollments/?semester=${selectedSemester.id}&page_size=500` : "",
    token,
    { enabled: !!selectedSemester?.id }
  );
  const enrollments = useMemo(() => {
    const raw = enrollmentsData?.results ?? enrollmentsData ?? [];
    return Array.isArray(raw) ? raw : [];
  }, [enrollmentsData]);

  const { data: usersData } = useFetchObjects(["users-curriculum"], "users/?page_size=300", token);
  const users = useMemo(() => usersData?.results ?? usersData ?? [], [usersData]);

  const { data: academicsSearchData } = useFetchObjects(
    ["academics-search", enrollSearch],
    enrollSearch.length >= 1 ? `academics/?search=${encodeURIComponent(enrollSearch)}&status=1&page_size=50` : "",
    token,
    { enabled: enrollSearch.length >= 1 }
  );
  const academicsForEnroll = useMemo(() => {
    const raw = academicsSearchData?.results ?? academicsSearchData ?? [];
    return Array.isArray(raw) ? raw : [];
  }, [academicsSearchData]);

  const { handleAdd: addTerm } = useAdd("terms", token, t("academic.termAdded", "Term added. Sem 1 and Sem 2 created."));
  const { handleUpdate: updateTerm } = useUpdate("terms", token);
  const { handleAdd: addSubject } = useAdd("subjects", token, t("academic.subjectAdded", "Subject added."));
  const { handleUpdate: updateSubject } = useUpdate("subjects", token);
  const { handleDelete: deleteSubject, ConfirmDialog: ConfirmDeleteSubject } = useDelete("subjects", token);

  const handleSaveTerm = (payload) => {
    if (editingTerm) {
      updateTerm({ id: editingTerm.id, data: payload });
    } else {
      addTerm({ ...payload, semester: null });
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ["terms-curriculum"] });
        queryClient.invalidateQueries({ queryKey: ["semesters-term"] });
      }, 1500);
    }
    setTermDialogOpen(false);
    setEditingTerm(null);
  };

  const handleOpenAddTerm = () => { setEditingTerm(null); setTermDialogOpen(true); };
  const handleOpenEditTerm = (term) => { setEditingTerm(term); setTermDialogOpen(true); };

  const handleOpenAddSubject = () => {
    setEditingSubject(null);
    setSubjectForm({ name: "", code: "", mentor: "" });
    setSubjectDialogOpen(true);
  };
  const handleOpenEditSubject = (sub) => {
    setEditingSubject(sub);
    setSubjectForm({
      name: sub.name || "",
      code: sub.code || "",
      mentor: sub.mentor ?? "",
    });
    setSubjectDialogOpen(true);
  };
  const handleSaveSubject = (e) => {
    e.preventDefault();
    const payload = {
      semester: selectedSemester?.id,
      name: subjectForm.name,
      code: subjectForm.code,
      mentor: subjectForm.mentor ? Number(subjectForm.mentor) : null,
    };
    if (editingSubject) updateSubject({ id: editingSubject.id, data: payload });
    else addSubject(payload);
    queryClient.invalidateQueries({ queryKey: ["subjects-semester", selectedSemester?.id] });
    setSubjectDialogOpen(false);
    setEditingSubject(null);
  };

  const handleBulkEnroll = async () => {
    if (!selectedSemester?.id || selectedAcademics.length === 0) return;
    setSavingEnroll(true);
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/semester-enrollments/bulk-create/`,
        { semester: selectedSemester.id, academics: selectedAcademics.map((a) => a.id) },
        { headers: token ? { Authorization: `Token ${token}` } : {} }
      );
      queryClient.invalidateQueries({ queryKey: ["semester-enrollments", selectedSemester?.id] });
      toast.success(t("academic.enrollmentAdded", "Enrollment added."));
      setEnrollDialogOpen(false);
      setSelectedAcademics([]);
      setEnrollSearch("");
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || t("academic.saveFailed", "Failed to save."));
    } finally {
      setSavingEnroll(false);
    }
  };

  const alreadyEnrolledIds = useMemo(() => new Set(enrollments.map((e) => e.academic?.id ?? e.academic).filter(Boolean)), [enrollments]);

  return (
    <Box sx={{ p: 3, width: "100%" }}>
      <ConfirmDeleteSubject />
      {!selectedTerm ? (
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Typography variant="h5" sx={{ fontWeight: "bold" }}>{t("academic.curriculum", "Curriculum")}</Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenAddTerm}>{t("academic.addTerm", "Add Term (Year)")}</Button>
          </Box>
          <Typography color="text.secondary" sx={{ mb: 2 }}>{t("academic.clickYearToManage", "Click a year to manage semesters, subjects and enrollments.")}</Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
            {terms.filter((t) => t.semester == null).map((term) => (
              <Card key={term.id} sx={{ minWidth: 180 }}>
                <CardActionArea onClick={() => setSelectedTerm(term)} sx={{ p: 2 }}>
                  <CardContent>
                    <Typography variant="h6">{term.name || `Year ${term.year}`}</Typography>
                    <Typography variant="body2" color="text.secondary">{term.start_date} – {term.end_date}</Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            ))}
          </Box>
        </Paper>
      ) : !selectedSemester ? (
        <Paper sx={{ p: 3 }}>
          <Button startIcon={<BackIcon />} onClick={() => setSelectedTerm(null)} sx={{ mb: 2 }}>{t("common.back", "Back")}</Button>
          <Typography variant="h5" sx={{ fontWeight: "bold", mb: 2 }}>{selectedTerm.name || `Year ${selectedTerm.year}`}</Typography>
          <Typography color="text.secondary" sx={{ mb: 2 }}>{t("academic.selectSemester", "Select a semester to add subjects and enroll students.")}</Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
            {semesters.length === 0 ? (
              <Typography color="text.secondary">{t("academic.noSemesters", "No semesters. Refresh the page after creating the term.")}</Typography>
            ) : (
              semesters.map((sem) => (
                <Card key={sem.id} sx={{ minWidth: 180 }}>
                  <CardActionArea onClick={() => setSelectedSemester(sem)} sx={{ p: 2 }}>
                    <CardContent>
                      <Typography variant="h6">{t("academic.semesterNum", { count: sem.semester_number }) || `Sem ${sem.semester_number}`}</Typography>
                      <Typography variant="body2" color="text.secondary">{sem.start_date} – {sem.end_date}</Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              ))
            )}
          </Box>
        </Paper>
      ) : (
        <Paper sx={{ p: 3 }}>
          <Button startIcon={<BackIcon />} onClick={() => setSelectedSemester(null)} sx={{ mb: 1 }}>{t("common.back", "Back")}</Button>
          <Button startIcon={<BackIcon />} onClick={() => { setSelectedSemester(null); setSelectedTerm(null); }} sx={{ mb: 2 }}>{t("academic.backToYears", "Back to years")}</Button>
          <Typography variant="h5" sx={{ fontWeight: "bold", mb: 2 }}>{selectedTerm.name || `Year ${selectedTerm.year}`} – {t("academic.semesterNum", { count: selectedSemester.semester_number }) || `Sem ${selectedSemester.semester_number}`}</Typography>

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1 }}>{t("academic.subjects", "Subjects")}</Typography>
            <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={handleOpenAddSubject} sx={{ mb: 1 }}>{t("academic.addSubject", "Add Subject")}</Button>
            <Table size="small">
              <TableHead><TableRow sx={{ bgcolor: "grey.200" }}>
                <TableCell><strong>{t("academic.subjectName", "Subject")}</strong></TableCell>
                <TableCell><strong>{t("academic.mentor", "Mentor")}</strong></TableCell>
                <TableCell align="right"><strong>{t("common.actions", "Actions")}</strong></TableCell>
              </TableRow></TableHead>
              <TableBody>
                {subjects.length === 0 ? <TableRow><TableCell colSpan={3}>{t("common.noRecords", "No records found.")}</TableCell></TableRow> : subjects.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>{row.name} {row.code ? `(${row.code})` : ""}</TableCell>
                    <TableCell>{row.mentor_display || "—"}</TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => handleOpenEditSubject(row)}><EditIcon fontSize="small" /></IconButton>
                      <IconButton size="small" onClick={() => deleteSubject(row.id)} color="error"><DeleteIcon fontSize="small" /></IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>

          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1 }}>{t("academic.enrollments", "Enrollments")}</Typography>
            <Button size="small" variant="outlined" startIcon={<EnrollIcon />} onClick={() => { setSelectedAcademics([]); setEnrollDialogOpen(true); }} sx={{ mb: 1 }}>{t("academic.enrollStudents", "Enroll students")}</Button>
            <Table size="small">
              <TableHead><TableRow sx={{ bgcolor: "grey.200" }}>
                <TableCell><strong>#</strong></TableCell>
                <TableCell><strong>{t("academic.student", "Student")}</strong></TableCell>
              </TableRow></TableHead>
              <TableBody>
                {enrollments.length === 0 ? <TableRow><TableCell colSpan={2}>{t("academic.noEnrollments", "No students enrolled. Click Enroll students to add.")}</TableCell></TableRow> : enrollments.map((e, idx) => (
                  <TableRow key={e.id} hover>
                    <TableCell>{idx + 1}</TableCell>
                    <TableCell>{e.academic_display || "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>

          <Dialog open={subjectDialogOpen} onClose={() => setSubjectDialogOpen(false)} maxWidth="sm" fullWidth>
            <DialogTitle>{editingSubject ? t("academic.editSubject", "Edit Subject") : t("academic.addSubject", "Add Subject")}</DialogTitle>
            <form onSubmit={handleSaveSubject}>
              <DialogContent>
                <Grid container spacing={2} sx={{ mt: 0.5 }}>
                  <Grid item xs={12}><TextField fullWidth required label={t("academic.subjectName", "Subject name")} value={subjectForm.name} onChange={(e) => setSubjectForm((p) => ({ ...p, name: e.target.value }))} size="small" /></Grid>
                  <Grid item xs={12}><TextField fullWidth label={t("academic.code", "Code")} value={subjectForm.code} onChange={(e) => setSubjectForm((p) => ({ ...p, code: e.target.value }))} size="small" /></Grid>
                  <Grid item xs={12}>
                    <TextField fullWidth select label={t("academic.mentor", "Mentor (teacher)")} value={subjectForm.mentor} onChange={(e) => setSubjectForm((p) => ({ ...p, mentor: e.target.value }))} size="small">
                      <MenuItem value="">—</MenuItem>
                      {users.map((u) => <MenuItem key={u.id} value={u.id}>{userDisplay(u)}</MenuItem>)}
                    </TextField>
                  </Grid>
                </Grid>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setSubjectDialogOpen(false)}>{t("common.cancel", "Cancel")}</Button>
                <Button type="submit" variant="contained" disabled={!subjectForm.name}>{t("common.save", "Save")}</Button>
              </DialogActions>
            </form>
          </Dialog>

          <Dialog open={enrollDialogOpen} onClose={() => { setEnrollDialogOpen(false); setSelectedAcademics([]); }} maxWidth="sm" fullWidth>
            <DialogTitle>{t("academic.enrollStudents", "Enroll students")}</DialogTitle>
            <DialogContent>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{t("academic.searchSelectStudents", "Search and select students to add to this semester.")}</Typography>
              <Autocomplete
                multiple
                options={academicsForEnroll.filter((a) => !alreadyEnrolledIds.has(a.id))}
                getOptionLabel={(opt) => opt.name_display || `${opt.first_name || ""} ${opt.last_name || ""}`.trim() || opt.trainee_id || `#${opt.id}`}
                value={selectedAcademics}
                onChange={(_, v) => setSelectedAcademics(v)}
                onInputChange={(_, v) => setEnrollSearch(v)}
                filterSelectedOptions
                renderInput={(params) => <TextField {...params} label={t("academic.searchStudent", "Search student")} size="small" />}
                sx={{ mb: 2 }}
              />
              <Typography variant="body2">{t("academic.selectedCount", "Selected: {{count}}", { count: selectedAcademics.length })}</Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setEnrollDialogOpen(false)}>{t("common.cancel", "Cancel")}</Button>
              <Button variant="contained" onClick={handleBulkEnroll} disabled={savingEnroll || selectedAcademics.length === 0}>
                {savingEnroll ? <CircularProgress size={24} /> : t("academic.addSelected", "Add selected")}
              </Button>
            </DialogActions>
          </Dialog>
        </Paper>
      )}

      <TermFormDialog open={termDialogOpen} onClose={() => { setTermDialogOpen(false); setEditingTerm(null); }} term={editingTerm} onSave={handleSaveTerm} />
    </Box>
  );
}
